import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { conversationsService } from "../api/conversations";
import type { ConversationMember, MemberRole } from "../api/types";
import { showError } from "../internal/shared/ErrorToast";
import { conversationKey } from "./useConversation";

export const membersKey = (conversationId: string) =>
  ["chat", "conversations", conversationId, "members"] as const;

/**
 * Backend exposes /conversations/{id}/members/ as a flat list (no pagination
 * — see services/chat/core/api/viewsets.py::ConversationMemberViewSet).
 * The Conversation list/retrieve endpoints also embed `members`, but using
 * the dedicated endpoint here lets us invalidate/refetch members in isolation
 * after add/remove/role-change without re-fetching the full conversation.
 */
export function useMembers(conversationId: string | undefined) {
  return useQuery({
    queryKey: conversationId ? membersKey(conversationId) : ["chat", "members", "_disabled"],
    queryFn: () => conversationsService.listMembers(conversationId!),
    enabled: Boolean(conversationId),
  });
}

export function useAddMember(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { user_sub: string; role?: MemberRole }) =>
      conversationsService.addMember(conversationId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: conversationKey(conversationId) });
      qc.invalidateQueries({ queryKey: membersKey(conversationId) });
    },
    onError: showError,
  });
}

export function useChangeMemberRole(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { userSub: string; role: MemberRole }) =>
      conversationsService.changeMemberRole(conversationId, vars.userSub, vars.role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: conversationKey(conversationId) });
      qc.invalidateQueries({ queryKey: membersKey(conversationId) });
    },
    onError: showError,
  });
}

export function useMuteConversation(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => conversationsService.mute(conversationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: membersKey(conversationId) });
    },
    onError: showError,
  });
}

export function useRemoveMember(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userSub: string) => conversationsService.removeMember(conversationId, userSub),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: conversationKey(conversationId) });
      qc.invalidateQueries({ queryKey: membersKey(conversationId) });
    },
    onError: showError,
  });
}

// Re-export type for callers that don't want to chase api/types
export type { ConversationMember };
